import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, USER_ROLES, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import PrivacyNoticeDialog from './components/common/PrivacyNoticeDialog';

// Auth components
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import MentorInvite from './components/auth/MentorInvite';
import MentorRegister from './components/auth/MentorRegister';

// Pages
import MentorsList from './components/mentors/MentorsList';
import MentorDetail from './components/mentors/MentorDetail';
import MentorProfile from './components/mentors/MentorProfile';
import MentorProfileEdit from './components/mentors/MentorProfileEdit';
import StudentProfile from './components/students/StudentProfile';
import StudentProfileEdit from './components/students/StudentProfileEdit';
import Unauthorized from './components/common/Unauthorized';

// CSS
import './App.css';
import './styles/theme.css';

// Home Router Component
const HomeRouter = () => {
  return <Navigate to="/mentors-list" replace />;
};

// Main App Content Component to manage privacy notice
const AppContent = () => {
  const { currentUser } = useAuth();
  const [showPrivacyNotice, setShowPrivacyNotice] = useState(false);

  useEffect(() => {
    // Show privacy notice when user logs in (first time per session)
    if (currentUser && !sessionStorage.getItem('privacyNoticeSeen')) {
      setShowPrivacyNotice(true);
    }
  }, [currentUser]);

  const handlePrivacyNoticeClose = () => {
    setShowPrivacyNotice(false);
    sessionStorage.setItem('privacyNoticeSeen', 'true');
  };

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/mentor-register" element={<MentorRegister />} />
        
        {/* Protected routes */}
        <Route 
          path="/mentors-list" 
          element={
            <ProtectedRoute>
              <MentorsList />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/mentor/:id" 
          element={
            <ProtectedRoute>
              <MentorDetail />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <MentorProfile />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/student-profile"
          element={
            <ProtectedRoute allowedRoles={[USER_ROLES.STUDENT]}>
              <StudentProfile />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/student-profile/edit" 
          element={
            <ProtectedRoute allowedRoles={[USER_ROLES.STUDENT]}>
              <StudentProfileEdit />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/mentor-profile" 
          element={
            <ProtectedRoute allowedRoles={[USER_ROLES.MENTOR]}>
              <MentorProfile />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/mentor-profile/edit" 
          element={
            <ProtectedRoute allowedRoles={[USER_ROLES.MENTOR]}>
              <MentorProfileEdit />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/invite-mentor" 
          element={
            <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.MENTOR]}>
              <MentorInvite />
            </ProtectedRoute>
          } 
        />
        
        {/* Unauthorized page */}
        <Route path="/unauthorized" element={<Unauthorized />} />
        
        {/* Redirect to home route based on user role */}
        <Route path="/" element={<HomeRouter />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      
      {/* Privacy Notice Dialog */}
      <PrivacyNoticeDialog 
        isOpen={showPrivacyNotice} 
        onClose={handlePrivacyNoticeClose} 
      />
    </>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App; 